import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, mapAuthError } from '../../../services/auth';
import { useAuth } from '../../../contexts/AuthContext';
import { ONBOARDING_PATH } from '../../workspace/navigation';
import PhoneAuthPanel from '../components/PhoneAuthPanel';
import { ArrowRight } from 'lucide-react';

const SignUp = () => {
  const [method, setMethod] = useState('phone'); // phone first for African markets
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [phoneFallbackNote, setPhoneFallbackNote] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (user && !authLoading) {
      navigate(ONBOARDING_PATH, { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handlePhoneUseEmail = ({ displayName: name, auto = false } = {}) => {
    if (name) setDisplayName(name);
    setMethod('email');
    setError('');
    setPhoneFallbackNote(
      auto
        ? 'Phone sign-up is unavailable. Continue with email below.'
        : ''
    );
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signUp(email, password, displayName);
      navigate(ONBOARDING_PATH, { replace: true });
    } catch (err) {
      setError(mapAuthError(err));
      setLoading(false);
    }
  };

  if (user) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand-100 px-4 py-12 font-body text-ink-900 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Link to="/" className="group inline-flex items-center gap-3">
            <div className="h-11 w-11 overflow-hidden rounded-full ring-1 ring-ink-900/15 transition-transform group-hover:scale-105">
              <img
                src="/logo192.png"
                alt="Spirara Logo"
                className="h-full w-full scale-110 object-cover"
              />
            </div>
            <span className="font-display text-2xl font-light tracking-tight text-ink-900">
              Spirara<span className="text-saffron-500">.</span>
            </span>
          </Link>
        </div>

        <div className="mb-8 text-center">
          <h1 className="font-display text-4xl font-light leading-tight tracking-tight text-ink-900 sm:text-5xl">
            Join Spirara
          </h1>
          <p className="mt-3 text-base leading-relaxed text-ink-700/80">
            Create your account and start taking bookings this week.
          </p>
        </div>

        <div className="bg-sand-50 p-8 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.45)] ring-1 ring-ink-900/10 sm:p-10">
          <div className="mb-6 flex bg-white ring-1 ring-ink-900/10">
            {[
              { id: 'phone', label: 'Phone' },
              { id: 'email', label: 'Email' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setMethod(item.id);
                  setError('');
                  setPhoneFallbackNote('');
                }}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  method === item.id
                    ? 'bg-ink-900 text-sand-50'
                    : 'text-ink-800 hover:bg-sand-100'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {method === 'phone' ? (
            <PhoneAuthPanel
              mode="signup"
              submitLabel="Send code"
              onSuccess={() => navigate(ONBOARDING_PATH, { replace: true })}
              onUseEmail={handlePhoneUseEmail}
            />
          ) : (
            <form className="space-y-6" onSubmit={handleEmailSubmit}>
              {phoneFallbackNote && (
                <div className="border border-saffron-300 bg-saffron-50 p-4">
                  <p className="text-sm text-ink-800">{phoneFallbackNote}</p>
                </div>
              )}
              {error && (
                <div className="border border-red-300 bg-red-50 p-4">
                  <div className="flex items-start">
                    <svg
                      className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">{error}</p>
                      {error.includes('already registered') && (
                        <Link
                          to="/login"
                          className="mt-2 inline-block text-sm text-red-700 underline hover:text-red-900"
                        >
                          Go to log in →
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="displayName"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                  >
                    Full name
                  </label>
                  <input
                    id="displayName"
                    name="displayName"
                    type="text"
                    required
                    className="w-full bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="Amara Okonkwo"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="w-full bg-white px-4 py-3 text-ink-900 placeholder-ink-600/40 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                  />
                  <p className="mt-1.5 text-xs text-ink-600/70">Minimum 6 characters</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group flex w-full items-center justify-center gap-2 bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  'Creating account...'
                ) : (
                  <>
                    Create account
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-ink-700/80">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-ink-900 underline decoration-saffron-500 decoration-2 underline-offset-4 transition-colors hover:text-ink-700"
              >
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
